import React, { useEffect, useState } from 'react';
import { PaymentRequestButtonElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { Post } from '../../api/server';
import { requiresPayment, validateCustomerInfo } from './util';

export default function TicketExpressCheckout({
  cart,
  totalAmountDue,
  event,
  customerInfo,
  affId,
  onSuccess,
  onError,
  onPaymentStart,
  onPaymentEnd,
}) {
  const stripe = useStripe();
  const elements = useElements();
  const [paymentRequest, setPaymentRequest] = useState(null);

  useEffect(() => {
    if (!stripe || !elements || !requiresPayment(totalAmountDue) || !event?._id) return undefined;

    const request = stripe.paymentRequest({
      country: 'US',
      currency: 'usd',
      total: {
        label: event.name || 'Ticket(s)',
        amount: Math.round(totalAmountDue * 100),
      },
      requestPayerName: true,
      requestPayerEmail: true,
      requestPayerPhone: true,
    });

    let active = true;
    request.canMakePayment().then((available) => {
      if (active && available) setPaymentRequest(request);
    });

    request.on('paymentmethod', async (paymentEvent) => {
      const validation = validateCustomerInfo(customerInfo);
      if (!validation.isValid) {
        paymentEvent.complete('fail');
        onError(Object.values(validation.errors)[0] || 'Please complete your info before paying.');
        return;
      }

      onPaymentStart();
      onError('');
      try {
        const response = await Post(`/events/v2/buyTickets/${event._id}`, {
          token: paymentEvent.paymentMethod,
          company: event?.company?._id || event?.company,
          first_name: validation.values.firstName,
          last_name: validation.values.lastName,
          phone: validation.values.phone,
          email: validation.values.email,
          tickets: cart,
          amountDue: Number(totalAmountDue).toFixed(2),
          affId: affId || null,
        });

        if (response?.paymentIntent?.status === 'requires_action') {
          paymentEvent.complete('fail');
          onError('Your bank requires additional authentication, which is not available for this checkout yet. Please use another card or try again.');
          return;
        }

        paymentEvent.complete('success');
        onSuccess(response);
      } catch (error) {
        paymentEvent.complete('fail');
        onError('We could not complete your ticket order. Please try again.');
      } finally {
        onPaymentEnd();
      }
    });

    return () => {
      active = false;
    };
  }, [affId, cart, customerInfo, elements, event, onError, onPaymentEnd, onPaymentStart, onSuccess, stripe, totalAmountDue]);

  if (!paymentRequest) return null;

  return (
    <div className="ticket-express">
      <p className="text-center">Express checkout</p>
      <PaymentRequestButtonElement options={{ paymentRequest }} />
      <p className="text-center" style={{ marginTop: 12 }}>or pay by card</p>
    </div>
  );
}
